/**
 * Gallery helpers — stays/events `images[]` may contain photos or MP4/MOV/WebM URLs.
 * Paw Story reels (`pawStoryVideoUrl`) are merged into the stay gallery at display time.
 */

const VIDEO_EXT = /\.(mp4|mov|webm|m4v)(\?|#|$)/i;

export function isGalleryVideoUrl(url) {
  if (typeof url !== 'string') return false;
  const u = url.trim();
  if (!u) return false;
  if (u.includes('/video/upload/')) return true;
  if (u.includes('/deliver-video')) return true;
  if (u.includes('/paw-stories/')) return true;
  if (VIDEO_EXT.test(u)) return true;
  return false;
}

export function isGalleryImageUrl(url) {
  if (typeof url !== 'string') return false;
  const u = url.trim();
  if (!u) return false;
  return !isGalleryVideoUrl(u);
}

function dedupeKey(url) {
  try {
    const u = new URL(url.trim());
    return `${u.origin}${u.pathname}`;
  } catch {
    return url.trim().split('?')[0];
  }
}

/**
 * @param {string} url
 * @param {string} [poster]
 * @returns {{ url: string, type: 'image'|'video', poster?: string }}
 */
export function galleryItemFromUrl(url, poster) {
  const u = String(url || '').trim();
  const type = isGalleryVideoUrl(u) ? 'video' : 'image';
  return poster && type === 'video' ? { url: u, type, poster } : { url: u, type };
}

/**
 * @param {{ url: string, type: 'image'|'video' }[]} items
 * @returns {string[]}
 */
export function galleryMediaToUrls(items) {
  return (items || []).map((item) => item.url).filter(Boolean);
}

/**
 * First image URL suitable as poster / search cover when gallery mixes video.
 * @param {object|null|undefined} stay
 * @returns {string|null}
 */
export function stayGalleryCoverImageUrl(stay) {
  if (!stay || typeof stay !== 'object') return null;
  const fromImages = (Array.isArray(stay.images) ? stay.images : []).find(isGalleryImageUrl);
  if (fromImages) return fromImages.trim();
  const fromFeatures = (Array.isArray(stay.features) ? stay.features : [])
    .map((f) => f?.image)
    .find(isGalleryImageUrl);
  if (fromFeatures) return String(fromFeatures).trim();
  return null;
}

/**
 * Ordered gallery for property page + modal (includes paw story reel when set).
 * @param {object|null|undefined} stay
 * @returns {Array<{ url: string, type: 'image'|'video', poster?: string }>}
 */
export function collectStayGalleryMedia(stay) {
  if (!stay || typeof stay !== 'object') return [];

  const seen = new Set();
  const out = [];
  const poster = stayGalleryCoverImageUrl(stay) || undefined;

  const push = (url, opts = {}) => {
    if (url == null || !String(url).trim()) return;
    const u = String(url).trim();
    const key = dedupeKey(u);
    if (seen.has(key)) return;
    seen.add(key);
    const item = galleryItemFromUrl(u, opts.poster ?? poster);
    out.push(item);
  };

  (Array.isArray(stay.images) ? stay.images : []).forEach((url) => push(url));

  const paw = stay.pawStoryVideoUrl && String(stay.pawStoryVideoUrl).trim();
  if (paw) push(paw, { poster });

  (Array.isArray(stay.founderReviews) ? stay.founderReviews : []).forEach((r) => {
    if (r?.image) push(r.image);
  });

  (Array.isArray(stay.rooms) ? stay.rooms : []).forEach((room) => {
    (Array.isArray(room?.images) ? room.images : []).forEach((url) => push(url));
  });

  (Array.isArray(stay.features) ? stay.features : []).forEach((f) => {
    if (f?.image) push(f.image);
  });

  return out;
}

/**
 * @param {object|null|undefined} event
 * @returns {Array<{ url: string, type: 'image'|'video', poster?: string }>}
 */
export function collectEventGalleryMedia(event) {
  if (!event || typeof event !== 'object') return [];

  const seen = new Set();
  const out = [];
  const poster =
    (Array.isArray(event.images) ? event.images : []).find(isGalleryImageUrl) || undefined;

  const push = (url) => {
    if (url == null || !String(url).trim()) return;
    const u = String(url).trim();
    const key = dedupeKey(u);
    if (seen.has(key)) return;
    seen.add(key);
    out.push(galleryItemFromUrl(u, poster));
  };

  (Array.isArray(event.images) ? event.images : []).forEach(push);
  return out;
}
